// src/hooks/useTicketOptions.ts
import { useMemo } from "react";
import {
  CATEGORY_OPTS,
  FUNCTION_ALL,
  MENU_OPTS,
  TEAMS_ALL,
  MATRIX,
  mapByIds,
  CATEGORY_OPTS_ADMIN,
} from "../types/ticketMatrix";
import { CategoryId, MenuId, RoleId, SupportTicketForm } from "../types/ticket";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  getAllowedMenusForRole,
  hasRolePermission,
  SidebarMenuId,
} from "../constants/roleAccess";

const isSidebarMenuId = (menuId: string): menuId is SidebarMenuId =>
  menuId !== "";

export function useTicketOptions(
  state: Pick<SupportTicketForm, "menu" | "category" | "func">,
  role: RoleId,
) {
  // Role-based filtering for Menu
  const menuOptions = useMemo(() => {
    const allowed = getAllowedMenusForRole(role);
    return MENU_OPTS.filter(
      (m) => isSidebarMenuId(m.id) && allowed.includes(m.id),
    );
  }, [role]);
  const currentRole = useSelector(
    (state: RootState) => {
      const role = state.userRoles?.currentRole;
      return typeof role === 'string' ? role.toLowerCase() : role;
    }
  );

  const categoryOptions = hasRolePermission(
    currentRole,
    "use_admin_categories",
  )
    ? CATEGORY_OPTS_ADMIN
    : CATEGORY_OPTS;

  const matrixEntry = useMemo(() => {
    if (!state.menu || !state.category) return null;
    const m = state.menu as MenuId;
    const c = state.category as CategoryId;
    return MATRIX[m]?.[c] ?? null;
  }, [state.menu, state.category]);

  const functionOptions = useMemo(() => {
    if (!matrixEntry) return [];
    return mapByIds(FUNCTION_ALL, matrixEntry.functions);
  }, [matrixEntry]);

  const teamOptions = useMemo(() => {
    if (!matrixEntry) return [];
    return mapByIds(TEAMS_ALL, matrixEntry.teams);
  }, [matrixEntry]);

  const isCategoryDisabled = !state.menu;
  const isFunctionDisabled = !state.category;
  const isAssignedToDisabled = !state.func;

  return {
    menuOptions,
    categoryOptions,
    functionOptions,
    teamOptions,
    isCategoryDisabled,
    isFunctionDisabled,
    isAssignedToDisabled,
  };
}
